import * as React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import Link from 'next/link';
import axios from 'axios';
import useSWR from 'swr';
import Head from 'next/head';

import config from 'config';
import { CardSlider } from 'components/PrivateMarketplace/CardSlider/CardSlider';
import { Breadcrumbs } from 'components/PrivateMarketplace/Breadcrumbs/Breadcrumbs';
import { selectCategories, selectHeaderDescriptionText } from 'lib/util/privateMarketplace';
import { toProductSummaryShape, ProductSummaryShape } from 'lib/util/productSummaryShape';
import { getBreadcrumbsLDJSONString } from 'lib/util/getBreadcrumbsLDJSONObject';
import { encodeCategoryNameForURI } from 'lib/util/encodeCategoryNameForURI';
import { ProductSummary } from 'models/product';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { ProductCard } from 'components/PrivateMarketplace/ProductCard/ProductCard';
import { ProductDetails } from 'components/ProductDetails/ProductDetails';
import { ReduxState } from 'ducks';
import { getProductPageOgpTags } from 'lib/util/getProductPageOgpTags';
import { toProductShape } from 'lib/util/productShape';
import { appendQueryString } from 'lib/util/appendQueryString';
import { FrequentlyViewedProducts } from 'components/FrequentlyViewedProducts/FrequentlyViewedProducts';
import { getWeekdayText } from 'lib/util/getWeekdayText';
import { useQueryString } from 'hooks/useQueryString';
import { getProductStructuredReviewData } from 'lib/util/getProductStructuredReviewData';
import { useCurrencyFormatter } from 'hooks/useCurrencyFormatter';

import clsx from 'clsx';
import { BookingWidgetThemeContext } from 'contexts/BookingWidgetThemeContext';
import { getThemeCssOverride } from 'components/Themes';

const fetcher = (url: string, apiKey: string, contentLanguage: string) =>
  axios
    .get(url, {
      headers: { 'x-api-key': apiKey, 'accept-language': contentLanguage },
    })
    .then((response) => response.data.products);

type Props = {
  host: string;
  ogpPromotionId?: string;
  showOgpHighlight?: boolean;
};

export const PmpProductDetails = ({ host, ogpPromotionId, showOgpHighlight }: Props) => {
  const { t, i18n } = useTranslation();
  const { apiKey } = React.useContext(ApiKeyContext);

  const product = useSelector((state: ReduxState) => state.server.product.product);
  const categories = useSelector(selectCategories);
  const headerText = useSelector(selectHeaderDescriptionText);
  const settings = useSelector((state: ReduxState) => state.server.settings.all);
  const reviews = useSelector((state: ReduxState) => state.universal.reviews.all);

  const relatedProductIds = product?.related_product_ids || [];
  const recommendedProductIds = product?.recommended_product_ids || [];

  const locale = i18n.language;

  const currencyFormatter = useCurrencyFormatter();

  const shouldFetchProductSummaries =
    recommendedProductIds.length > 0 || relatedProductIds.length > 0;
  const { data } = useSWR<ProductSummary[]>(
    shouldFetchProductSummaries ? [`${config.apiUrl}/products`, apiKey, locale] : null,
    fetcher
  );

  const queryString = useQueryString();

  const productSummaries = data?.map?.(toProductSummaryShape);

  const recommendedProducts: ProductSummaryShape[] = [];
  recommendedProductIds.forEach((productId) => {
    const product = productSummaries?.find((p) => p.id === productId);
    if (product) {
      recommendedProducts.push(product);
    }
  });

  const relatedProducts: ProductSummaryShape[] = [];
  relatedProductIds.forEach((productId) => {
    const product = productSummaries?.find((p) => p.id === productId);
    if (product) {
      relatedProducts.push(product);
    }
  });

  const productId = product?.id;
  const productCategories = productId
    ? categories.filter((category) => (category.product_ids || []).includes(productId))
    : [];

  const category = productCategories.length > 0 ? productCategories[0] : null;
  const categoryDisplayName = category?.display_name || category?.name;
  const categoryName = category?.name;

  const title = `${product?.product_name || ''} | ${headerText}`;
  const description = product?.description || '';
  const dayOfWeek = product?.operating_days_of_week.length
    ? product.operating_days_of_week.map((weekday) => getWeekdayText(weekday, t)).join(',')
    : '';
  let lowestPrice = product?.lowest_price_gross || '';
  if (lowestPrice) {
    lowestPrice = currencyFormatter.format(lowestPrice);
  }

  let ogpTags;
  if (product) {
    ogpTags = getProductPageOgpTags(
      toProductShape(product),
      showOgpHighlight || false,
      ogpPromotionId || '',
      headerText,
      settings?.supplier_name || '',
      locale,
      t,
      currencyFormatter
    );
  }

  const twitter = (settings?.sns_ids || []).find((snsId) => {
    return snsId?.key === 'twitter';
  });

  const bookingWidgetTheme = React.useContext(BookingWidgetThemeContext);

  if (!product) {
    return (
      <div>
        <Typography variant="h5" component="h3">
          {t('Product not found')}
        </Typography>
      </div>
    );
  }

  return (
    <>
      <Head>
        <title>{title}</title>
        {product?.booking_widget_settings?.is_hidden_from_product_list_and_search && (
          <meta name="robots" content="noindex" />
        )}
        {description && <meta name="description" content={description} />}
        {ogpTags &&
          Object.entries(ogpTags).map(([key, value]) => (
            <meta key={key} property={key} content={value} />
          ))}
        {dayOfWeek && (
          <meta
            name="nutmeg:operating_days_of_week"
            content={t('Days of Week') + ':' + dayOfWeek}
          />
        )}
        {lowestPrice && <meta name="nutmeg:lowest_price_gross" content={lowestPrice} />}
        {twitter && twitter?.id && <meta name="twitter:site" content={`@${twitter.id}`} />}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: getBreadcrumbsLDJSONString(host, [
              {
                name: t('HOME'),
                relativeLink: '/top',
              },
              ...(categoryName && categoryDisplayName
                ? [
                    {
                      name: categoryDisplayName,
                      relativeLink: `/top/${encodeCategoryNameForURI(categoryName)}`,
                    },
                  ]
                : []),
              {
                name: product?.product_name || '',
              },
            ]),
          }}
        />
        {product != null && reviews != null && (product?.review_count ?? 0) > 0 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(getProductStructuredReviewData(product, reviews)),
            }}
          />
        )}
      </Head>
      <Breadcrumbs
        pageTitle={product?.product_name || ''}
        parentLinks={[
          {
            href: appendQueryString('/top', queryString),
            text: t('HOME'),
          },
          ...(categoryName && categoryDisplayName
            ? [
                {
                  href: appendQueryString(
                    `/top/${encodeCategoryNameForURI(categoryName)}`,
                    queryString
                  ),
                  text: categoryDisplayName,
                },
              ]
            : []),
        ]}
      />

      <ProductDetails />
      {relatedProducts.length > 0 && (
        <div
          className={clsx('base-related', getThemeCssOverride(bookingWidgetTheme, 'base-related'))}
        >
          <h3 className="base-related__headline">{t('Related')}</h3>
          <CardSlider
            centerNarrowContent={true}
            data={relatedProducts}
            renderElement={(product) => (
              <Link href={appendQueryString(`/top/products/${product.id}`, queryString)}>
                <ProductCard displayLabelTypes={['PROMOTION']} product={product} />
              </Link>
            )}
          />
        </div>
      )}
      {recommendedProducts.length > 0 && (
        <div
          className={clsx('base-related', getThemeCssOverride(bookingWidgetTheme, 'base-related'))}
        >
          <h3 className="base-related__headline">{t('Recommended')}</h3>
          <CardSlider
            centerNarrowContent={true}
            data={recommendedProducts}
            renderElement={(product) => (
              <Link href={appendQueryString(`/top/products/${product.id}`, queryString)}>
                <ProductCard displayLabelTypes={['PROMOTION']} product={product} />
              </Link>
            )}
          />
        </div>
      )}
      <FrequentlyViewedProducts />
    </>
  );
};
